/**
 * The Period control: a from–to month picker, laid out like a booking calendar but in
 * months. Click a start month, then an end month; the months between fill in as you
 * hover, and the second click applies the range.
 *
 * It replaced two "Any / Jan … Aug" dropdowns under a hard-coded year. Those could not
 * say which year a month was in, so a bundle crossing New Year would have offered two
 * "Jan"s, and a start picked after the end was accepted and quietly emptied the page.
 * Here every month is shown in its year, the years page with the arrows, a start
 * after the end is swapped, and a month the bundle holds no data for cannot be picked.
 *
 * Stored as 'YYYY-MM' keys, so nothing about it is tied to a year.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { ALL } from '@/data/filters'
import { monthSpan } from '@/format'

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const key = (year: number, ix: number) => `${year}-${String(ix + 1).padStart(2, '0')}`

export interface MonthRangePickerProps {
  /** Every month the bundle carries, 'YYYY-MM', ascending. */
  months: string[]
  /** Months not closed at export; marked, still pickable. */
  openMonths: string[]
  /** Of those, the closed months held open because their payroll is still loading. */
  payrollPending?: string[]
  /** 'YYYY-MM' or ALL. */
  from: string
  to: string
  /** Where an unset end stops — the last closed month — or null for the last month. */
  closedThrough?: string | null
  onChange: (from: string, to: string) => void
}

const trigger: React.CSSProperties = {
  fontFamily: 'var(--sans)',
  fontSize: 'var(--t-table)',
  padding: '5px 7px',
  borderRadius: 6,
  border: '1px solid var(--rule-2)',
  background: 'var(--surface)',
  color: 'var(--ink)',
  minWidth: 0,
  width: '100%',
  textAlign: 'left',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: 6,
}

const navBtn: React.CSSProperties = {
  border: '1px solid var(--rule-2)',
  background: 'var(--surface)',
  color: 'var(--ink)',
  borderRadius: 6,
  width: 28,
  height: 26,
  cursor: 'pointer',
  fontSize: 14,
  lineHeight: 1,
}

const presetBtn: React.CSSProperties = {
  border: '1px solid var(--rule-2)',
  background: 'var(--surface)',
  color: 'var(--ink)',
  borderRadius: 999,
  padding: '3px 9px',
  fontSize: 'var(--t-micro)',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
}

/** One end of the range, boxed like a booking form's check-in / check-out. */
function End({
  label,
  value,
  empty,
  active,
}: {
  label: string
  value: string | null
  empty: string
  /** The end the next click sets. */
  active: boolean
}) {
  return (
    <div
      style={{
        border: `1px solid ${active ? 'var(--accent)' : 'var(--rule-2)'}`,
        borderRadius: 6,
        padding: '4px 8px',
        display: 'grid',
      }}
    >
      <span className="eyebrow" style={{ fontSize: 'var(--t-micro)' }}>
        {label}
      </span>
      <span style={{ fontSize: 'var(--t-table)', color: value ? 'var(--ink)' : 'var(--ink-3)' }}>
        {value ? monthSpan([value]) : empty}
      </span>
    </div>
  )
}

/**
 * The applied range as the closed control reads it. An unset end reads as where it
 * really stops: the last closed month, when the bundle ends in an open one.
 */
export function rangeSummary(
  months: string[],
  from: string,
  to: string,
  closedThrough: string | null = null,
): string {
  const defaultHi =
    closedThrough !== null && (from === ALL || from <= closedThrough)
      ? closedThrough
      : months[months.length - 1]
  if (from === ALL && to === ALL)
    return closedThrough !== null && defaultHi === closedThrough
      ? `${monthSpan([months[0] ?? closedThrough, closedThrough])} · closed months`
      : 'All months'
  const lo = from === ALL ? months[0] : from
  const hi = to === ALL ? defaultHi : to
  if (!lo || !hi) return 'All months'
  return monthSpan(lo <= hi ? [lo, hi] : [hi, lo])
}

export function MonthRangePicker({
  months,
  openMonths,
  payrollPending = [],
  from,
  to,
  closedThrough = null,
  onChange,
}: MonthRangePickerProps) {
  const [open, setOpen] = useState(false)
  /** First click of a pick in progress; null when none is. */
  const [anchor, setAnchor] = useState<string | null>(null)
  const [hover, setHover] = useState<string | null>(null)
  const box = useRef<HTMLDivElement>(null)

  const available = useMemo(() => new Set(months), [months])
  const years = useMemo(() => {
    const ys = months.map((m) => Number(m.slice(0, 4)))
    if (ys.length === 0) return [new Date().getFullYear()]
    const out: number[] = []
    for (let y = Math.min(...ys); y <= Math.max(...ys); y++) out.push(y)
    return out
  }, [months])

  const applied = {
    lo: from === ALL ? null : from,
    hi: to === ALL ? null : to,
  }
  // The year the grid opens on: the applied start's, else the latest year with data.
  const startYear = Number((applied.lo ?? months[months.length - 1] ?? '').slice(0, 4)) || years[years.length - 1]!
  const [year, setYear] = useState(startYear)

  const close = () => {
    setOpen(false)
    setAnchor(null)
    setHover(null)
  }

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) close()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const apply = (a: string, b: string) => {
    const [lo, hi] = a <= b ? [a, b] : [b, a]
    onChange(lo, hi)
    close()
  }

  const pick = (m: string) => {
    if (anchor === null) {
      setAnchor(m)
      return
    }
    apply(anchor, m)
  }

  // What is highlighted: the pick in progress (anchor → hover) or the applied range.
  const shown = (() => {
    if (anchor !== null) {
      const other = hover ?? anchor
      return anchor <= other ? { lo: anchor, hi: other } : { lo: other, hi: anchor }
    }
    const defaultHi =
      closedThrough !== null && (applied.lo === null || applied.lo <= closedThrough)
        ? closedThrough
        : months[months.length - 1]
    if (applied.lo === null && applied.hi === null && closedThrough === null) return null
    return {
      lo: applied.lo ?? months[0] ?? '',
      hi: applied.hi ?? defaultHi ?? '',
    }
  })()

  const isSet = from !== ALL || to !== ALL
  const latestYear = years[years.length - 1]!
  const lastN = (n: number) => months.slice(-n)
  // The running month, offered by name so choosing it is deliberate.
  const inProgress = openMonths.filter((m) => available.has(m))
  const presets: { label: string; span: string[] }[] = [
    ...(inProgress.length
      ? [
          {
            label: `Include ${monthSpan(inProgress)} (in progress)`,
            span: [months[0]!, inProgress[inProgress.length - 1]!],
          },
        ]
      : []),
    { label: 'Latest month', span: lastN(1) },
    { label: 'Last 3 months', span: lastN(3) },
    { label: 'Last 6 months', span: lastN(6) },
    ...years.map((y) => ({
      label: y === latestYear && y === new Date().getFullYear() ? `${y} to date` : String(y),
      span: months.filter((m) => m.startsWith(`${y}-`)),
    })),
  ].filter((p) => p.span.length > 0)

  const yIx = years.indexOf(year)

  return (
    <div ref={box} style={{ display: 'grid', gap: 4, position: 'relative' }}>
      <span className="eyebrow">Month date range</span>
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => (open ? close() : (setYear(startYear), setOpen(true)))}
        style={{ ...trigger, borderColor: isSet ? 'var(--accent)' : 'var(--rule-2)' }}
      >
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: isSet ? 'var(--ink)' : 'var(--ink-3)',
          }}
        >
          {rangeSummary(months, from, to, closedThrough)}
        </span>
        <span className="mono" style={{ marginLeft: 'auto', color: 'var(--ink-3)' }} aria-hidden="true">
          {open ? '▴' : '▾'}
        </span>
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Choose a month range"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            zIndex: 30,
            marginTop: 4,
            width: 'min(300px, calc(100vw - 32px))',
            background: 'var(--surface)',
            border: '1px solid var(--rule-2)',
            borderRadius: 7,
            boxShadow: 'var(--shadow)',
            padding: 10,
            display: 'grid',
            gap: 10,
          }}
        >
          {/* From → To, as a booking form shows check-in and check-out. */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 6, alignItems: 'center' }}>
            <End label="From" value={shown?.lo ?? null} empty="Earliest" active={anchor === null} />
            <span aria-hidden="true" style={{ color: 'var(--ink-3)' }}>
              →
            </span>
            <End label="To" value={shown?.hi ?? null} empty="Latest" active={anchor !== null} />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <button
              type="button"
              aria-label="Previous year"
              disabled={yIx <= 0}
              onClick={() => setYear(years[yIx - 1]!)}
              style={{ ...navBtn, color: yIx <= 0 ? 'var(--ink-3)' : 'var(--ink)', cursor: yIx <= 0 ? 'default' : 'pointer' }}
            >
              ‹
            </button>
            <strong style={{ fontSize: 'var(--t-body)' }} aria-live="polite">
              {year}
            </strong>
            <button
              type="button"
              aria-label="Next year"
              disabled={yIx >= years.length - 1}
              onClick={() => setYear(years[yIx + 1]!)}
              style={{
                ...navBtn,
                color: yIx >= years.length - 1 ? 'var(--ink-3)' : 'var(--ink)',
                cursor: yIx >= years.length - 1 ? 'default' : 'pointer',
              }}
            >
              ›
            </button>
          </div>

          <div
            role="grid"
            aria-label={`Months of ${year}`}
            style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4 }}
            onMouseLeave={() => setHover(null)}
          >
            {MONTH_ABBR.map((abbr, ix) => {
              const m = key(year, ix)
              const has = available.has(m)
              const inside = shown !== null && m >= shown.lo && m <= shown.hi
              const edge = shown !== null && (m === shown.lo || m === shown.hi)
              const isOpen = openMonths.includes(m)
              return (
                <button
                  key={m}
                  type="button"
                  role="gridcell"
                  disabled={!has}
                  aria-selected={inside}
                  aria-label={`${abbr} ${year}${isOpen ? ', not closed at export' : ''}${has ? '' : ', no data'}`}
                  title={
                    !has
                      ? 'No data for this month in the current bundle'
                      : payrollPending.includes(m)
                        ? 'Payroll still loading — every claim is in, part of the labor is not, so cost per claim reads too low'
                        : isOpen
                          ? 'Not closed at export — the rate is real, the volume is not final'
                          : undefined
                  }
                  onClick={() => pick(m)}
                  onMouseEnter={() => has && setHover(m)}
                  onFocus={() => has && setHover(m)}
                  style={{
                    padding: '7px 0',
                    borderRadius: 6,
                    fontSize: 'var(--t-table)',
                    fontFamily: 'var(--sans)',
                    cursor: has ? 'pointer' : 'default',
                    border: `1px ${has ? 'solid' : 'dashed'} ${edge ? 'var(--accent)' : 'var(--rule-2)'}`,
                    background: edge ? 'var(--accent)' : inside ? 'var(--accent-soft)' : 'var(--surface)',
                    // Surface on the solid accent, accent-ink on the soft fill: the pairs
                    // the tokens are contrast-checked for, in both themes.
                    color: edge
                      ? 'var(--surface)'
                      : inside
                        ? 'var(--accent-ink)'
                        : has
                          ? 'var(--ink)'
                          : 'var(--ink-3)',
                    fontWeight: edge ? 600 : 400,
                  }}
                >
                  {abbr}
                  {isOpen && has ? ' *' : ''}
                </button>
              )
            })}
          </div>

          <p style={{ margin: 0, fontSize: 'var(--t-micro)', color: 'var(--ink-3)' }}>
            {anchor === null
              ? 'Pick a start month, then an end month. Same month twice for one month.'
              : `From ${monthSpan([anchor])} — now pick the end month.`}
            {openMonths.length > 0 &&
              ' * still in progress — left out unless you pick it: pay and billing land on different days, so its figures swing wildly until it closes.'}
            {payrollPending.length > 0 &&
              ` ${monthSpan(payrollPending)} has ended but is held the same way: its payroll has not all loaded yet, so its labor is short of its claims.`}
          </p>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {presets.map((p) => (
              <button
                key={p.label}
                type="button"
                style={presetBtn}
                onClick={() => apply(p.span[0]!, p.span[p.span.length - 1]!)}
              >
                {p.label}
              </button>
            ))}
            <button
              type="button"
              style={{ ...presetBtn, marginLeft: 'auto', color: isSet ? 'var(--accent)' : 'var(--ink-3)' }}
              onClick={() => {
                onChange(ALL, ALL)
                close()
              }}
            >
              All months
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
